import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { request } from '../api/client';

interface LogItem {
  id: string;
  level: 'warn' | 'error' | 'fatal';
  source: string;
  message: string;
  context: unknown;
  requestId: string | null;
  errorStack: string | null;
  createdAt: string;
}

interface LogsPage {
  items: LogItem[];
  total: number;
}

const LEVEL_BADGE: Record<string, string> = {
  warn: 'text-sara-warning',
  error: 'text-sara-danger',
  fatal: 'text-sara-danger font-bold',
};

export function SystemLogsPage(): JSX.Element {
  // Lê o filtro da URL pra o atalho "Avisos" do menu abrir já filtrado em
  // `source=alerta` — que é a fila única de coisas que precisam da dona.
  const inicial = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    // `since` também vem da URL (o Escritório Aura manda 1440). Aberto já filtrado (atalho
    // "Avisos" ou link de fora) sem `since`: 24 h, porque o alerta pode ter horas e com
    // "última 1h" a tela abria vazia (20/10/2026: alerta da Caroline das 16h, aberto às 21h).
    const sinceUrl = params.get('since');
    const validos = ['15', '60', '360', '1440', ''];
    const since =
      sinceUrl !== null && validos.includes(sinceUrl) ? sinceUrl : params.get('source') ? '1440' : '60';
    return { source: params.get('source') ?? '', level: params.get('level') ?? '', since };
  }, []);
  const [level, setLevel] = useState(inicial.level);
  const [source, setSource] = useState(inicial.source);
  const [search, setSearch] = useState('');
  const [since, setSince] = useState(inicial.since);

  const { data, isLoading, isError, refetch, isFetching } = useQuery<LogsPage>({
    queryKey: ['system-logs', level, source, search, since],
    queryFn: () => {
      const params = new URLSearchParams();
      if (level) params.set('level', level);
      if (source) params.set('source', source);
      if (search) params.set('search', search);
      if (since) params.set('sinceMinutes', since);
      params.set('pageSize', '100');
      return request<LogsPage>(`/admin/system-logs?${params.toString()}`);
    },
    refetchInterval: 15_000,
  });

  return (
    <section aria-labelledby="logs-heading" className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h1 id="logs-heading" className="text-2xl font-semibold">
          Logs do Sistema
        </h1>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          {isFetching ? 'atualizando…' : 'Atualizar'}
        </button>
      </div>

      <div className="card grid grid-cols-1 sm:grid-cols-4 gap-3">
        <label className="block">
          <span className="label">Nível</span>
          <select className="input" value={level} onChange={(e) => setLevel(e.target.value)}>
            <option value="">Todos (warn+)</option>
            <option value="warn">warn</option>
            <option value="error">error</option>
            <option value="fatal">fatal</option>
          </select>
        </label>
        <label className="block">
          <span className="label">Source contém</span>
          <input
            type="text"
            className="input"
            placeholder="backend, worker, etc"
            value={source}
            onChange={(e) => setSource(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="label">Busca em mensagem</span>
          <input
            type="search"
            className="input"
            placeholder="texto…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="label">Janela (min)</span>
          <select className="input" value={since} onChange={(e) => setSince(e.target.value)}>
            <option value="15">últimos 15 min</option>
            <option value="60">última 1h</option>
            <option value="360">últimas 6h</option>
            <option value="1440">últimas 24h</option>
            <option value="">todas</option>
          </select>
        </label>
      </div>

      {isLoading && (
        <div className="card animate-pulse" role="status" aria-live="polite">
          <div className="h-4 bg-sara-surface-hover rounded w-2/3 mb-2" />
          <div className="h-4 bg-sara-surface-hover rounded w-full mb-2" />
          <div className="h-4 bg-sara-surface-hover rounded w-3/4" />
        </div>
      )}

      {isError && (
        <div role="alert" className="card border-sara-danger">
          <p className="text-sara-danger">Erro carregando logs.</p>
        </div>
      )}

      {data && (
        <p className="text-xs text-sara-text/50">
          {data.total} registro(s){' '}
          {data.total > data.items.length && `(mostrando ${data.items.length})`}
        </p>
      )}

      {data?.items.length === 0 && (
        <div className="card text-sara-text-muted text-sm">Nenhum log nesse filtro. 🎉</div>
      )}

      <ul className="space-y-2">
        {data?.items.map((log) => (
          <li key={log.id} className="card text-sm">
            <header className="flex items-baseline gap-3 flex-wrap">
              <span className={LEVEL_BADGE[log.level]}>{log.level.toUpperCase()}</span>
              <code className="text-xs text-sara-text-muted">{log.source}</code>
              <span className="text-xs text-sara-text-subtle ml-auto">
                {new Date(log.createdAt).toLocaleString('pt-BR')}
              </span>
            </header>
            <p className="mt-1 break-words">{log.message}</p>
            {log.requestId && (
              <p className="text-xs text-sara-text-subtle mt-1">
                requestId: <code>{log.requestId}</code>
              </p>
            )}
            {log.context !== null && (
              <details className="mt-2">
                <summary className="text-xs text-sara-text-muted cursor-pointer">contexto</summary>
                <pre className="mt-1 text-xs text-sara-text-muted overflow-x-auto">
                  {JSON.stringify(log.context, null, 2)}
                </pre>
              </details>
            )}
            {log.errorStack && (
              <details className="mt-2">
                <summary className="text-xs text-sara-text-muted cursor-pointer">
                  stack trace
                </summary>
                <pre className="mt-1 text-xs text-sara-text/50 overflow-x-auto">
                  {log.errorStack}
                </pre>
              </details>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
